import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';

/**
 * VersionChecker - Auto Update Detection
 * Detects when a new app version is deployed by comparing script file hashes
 * and prompts users to reload.
 */
export default function VersionChecker() {
  const [hasUpdate, setHasUpdate] = useState(false);

  useEffect(() => {
    // Extract script sources from DOM
    const getScriptSources = (doc) => {
      return Array.from(doc.getElementsByTagName('script'))
        .map((s) => s.src)
        .filter((src) => src && src.includes('/assets/') && !src.includes('chrome-extension'));
    };

    const initialScripts = JSON.stringify(getScriptSources(document));

    const checkForUpdates = async () => {
      try {
        // Fetch latest HTML with no cache
        const response = await fetch('/', {
          cache: 'no-store',
          headers: { Pragma: 'no-cache' },
        });

        if (!response.ok) return;

        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const latestScripts = JSON.stringify(getScriptSources(doc));

        // Compare scripts - if different, update available
        if (initialScripts !== latestScripts && latestScripts !== '[]') {
          setHasUpdate(true);
        }
      } catch (error) {
        console.error('Failed to check for updates:', error);
      }
    };

    // Check every 5 minutes + on window focus
    const intervalId = setInterval(checkForUpdates, 5 * 60 * 1000);
    window.addEventListener('focus', checkForUpdates);
    const initialTimeout = setTimeout(checkForUpdates, 10000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(initialTimeout);
      window.removeEventListener('focus', checkForUpdates);
    };
  }, []);

  useEffect(() => {
    if (hasUpdate) {
      toast.custom(
        (t) => (
          <div className="bg-slate-900 text-white p-4 rounded-lg shadow-xl border border-slate-700 flex items-center justify-between gap-4 min-w-[320px]">
            <div className="flex items-center gap-3">
              <RefreshCw className="w-5 h-5 text-blue-400" />
              <div>
                <p className="font-semibold text-sm">Update Available</p>
                <p className="text-xs text-slate-400">A new version is ready.</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              Reload Now
            </button>
          </div>
        ),
        {
          duration: Infinity,
          id: 'app-update-toast',
          position: 'bottom-right',
        }
      );
    }
  }, [hasUpdate]);

  return null;
}
